import debounce from "lodash-es/debounce";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type UseDatabaseSearchParams = {
  initialValue: string;
  isLegacySearch: boolean;
  onSearchChange: (value: string) => void;
};

/**
 * Custom hook to manage database search input with debouncing.
 * - Handles immediate input updates for responsive UI
 * - Debounces server-side search requests
 * - Skips debounce for legacy (client-side) search
 * - Uses isPendingRef to prevent race conditions when typing fast
 */
export const useDatabaseSearch = ({
  initialValue,
  isLegacySearch,
  onSearchChange,
}: UseDatabaseSearchParams) => {
  const [inputValue, setInputValue] = useState(initialValue);
  // Track if there's a pending debounced change to prevent external sync overwriting user input
  const isPendingRef = useRef(false);

  const debouncedSearchChange = useMemo(
    () =>
      debounce((value: string) => {
        isPendingRef.current = false;
        onSearchChange(value);
      }, 300),
    [onSearchChange]
  );

  // Only sync from external when there's no pending debounced change
  // This prevents the race condition where fast typing gets overwritten
  useEffect(() => {
    if (!isPendingRef.current) {
      setInputValue(initialValue);
    }
  }, [initialValue]);

  useEffect(() => {
    return () => {
      debouncedSearchChange.cancel();
    };
  }, [debouncedSearchChange]);

  const handleChange = useCallback(
    (value: string) => {
      setInputValue(value);

      if (!value.trim()) {
        debouncedSearchChange.cancel();
        isPendingRef.current = false;
        onSearchChange("");
        return;
      }

      if (isLegacySearch) {
        debouncedSearchChange.cancel();
        isPendingRef.current = false;
        onSearchChange(value);
        return;
      }

      isPendingRef.current = true;
      debouncedSearchChange(value);
    },
    [debouncedSearchChange, isLegacySearch, onSearchChange]
  );

  return {
    inputValue,
    handleChange,
  };
};
